import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldPlus } from "lucide-react";
import { formatAccountNo, useAccounts } from "@/lib/accounts";
import { useCompanySettings, useUpdateCompanySettings } from "@/lib/companySettings";
import { formatMoment } from "@/lib/format";
import { type OwnChat, errorText, useBringSecondAdmin } from "@/lib/ownChats";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

/**
 * A channel's second admin: another of the company's accounts, with the right
 * to name admins — should the creator be banned, the channel is not lost.
 */
export function SecondAdminCard({ chat }: { chat: OwnChat }) {
  const { t } = useTranslation();
  const bring = useBringSecondAdmin();
  const state = chat.second_admin_state;
  const when = chat.second_admin_due_at ? formatMoment(chat.second_admin_due_at) : "";
  const who = chat.second_admin_seq_no !== null ? formatAccountNo(chat.second_admin_seq_no) : "";

  const start = async () => {
    try {
      await bring.mutateAsync({ id: chat.id });
      pushToast({ kind: "success", text: t("own_chats.second_admin.started") });
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("own_chats.action_error")) });
    }
  };

  return (
    <section className="space-y-3 rounded-xl border border-border bg-bg-primary px-4 py-3">
      <div>
        <h3 className="text-sm font-semibold text-fg-primary">
          {t("own_chats.second_admin.title")}
        </h3>
        <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">
          {t("own_chats.second_admin.hint")}
        </p>
      </div>
      {state === "done" ? (
        <p className="text-sm text-success">{t("own_chats.second_admin.done", { who })}</p>
      ) : state === "waiting" ? (
        <p className="text-sm text-fg-secondary">{t("own_chats.second_admin.waiting", { when })}</p>
      ) : state === "promoting" ? (
        <p className="text-sm text-fg-secondary">
          {t("own_chats.second_admin.promoting", { who, when })}
        </p>
      ) : state === "failed" ? (
        <Alert tone="warning">
          {t("own_chats.second_admin.failed")}
          {chat.second_admin_detail ? ` ${chat.second_admin_detail}` : ""}
        </Alert>
      ) : (
        <p className="text-sm text-fg-muted">{t("own_chats.second_admin.none")}</p>
      )}
      {(state === null || state === "failed") && (
        <Button
          size="sm"
          variant="outline"
          loading={bring.isPending}
          onClick={start}
          iconLeft={<ShieldPlus size={15} strokeWidth={2} aria-hidden="true" />}
        >
          {t(state === "failed" ? "own_chats.second_admin.retry" : "own_chats.second_admin.start")}
        </Button>
      )}
    </section>
  );
}

/** The company's word on second admins for the channels the panel makes. */
export function SecondAdminSetting() {
  const { t } = useTranslation();
  const settings = useCompanySettings();
  const update = useUpdateCompanySettings();
  const accounts = useAccounts();
  const held = settings.data?.own_chats;
  const [minutes, setMinutes] = useState("30");
  useEffect(() => {
    if (held) setMinutes(String(held.second_admin_after_minutes));
  }, [held]);

  if (!held) return null;
  const save = async (patch: Partial<typeof held>) => {
    try {
      await update.mutateAsync({ own_chats: patch });
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("own_chats.action_error")) });
    }
  };
  const usable = (accounts.data ?? []).filter((a) => a.status === "active");

  return (
    <section className="space-y-3 rounded-2xl border border-border bg-bg-secondary px-5 py-4">
      <div>
        <h2 className="text-sm font-semibold text-fg-primary">
          {t("own_chats.second_admin.setting_title")}
        </h2>
        <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">
          {t("own_chats.second_admin.setting_hint")}
        </p>
      </div>
      <Switch
        checked={held.second_admin}
        onCheckedChange={(on) => void save({ second_admin: on })}
        label={t("own_chats.second_admin.setting_on")}
      />
      {held.second_admin && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="second_admin_after">{t("own_chats.second_admin.after")}</Label>
            <Input
              id="second_admin_after"
              type="number"
              min={5}
              max={1440}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              onBlur={() => {
                const n = Math.min(1440, Math.max(5, Number(minutes) || 30));
                setMinutes(String(n));
                if (n !== held.second_admin_after_minutes)
                  void save({ second_admin_after_minutes: n });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("own_chats.second_admin.account")}</Label>
            <SimpleSelect
              value={held.second_admin_account_id ?? "auto"}
              onValueChange={(value) =>
                void save({ second_admin_account_id: value === "auto" ? null : value })
              }
              options={[
                { value: "auto", label: t("own_chats.second_admin.account_auto") },
                ...usable.map((a) => ({
                  value: a.id,
                  label: `${formatAccountNo(a.seq_no)} · ${a.display_name}`,
                })),
              ]}
            />
          </div>
        </div>
      )}
    </section>
  );
}
